import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  X, Instagram, Facebook, Sparkles, Loader2, Send,
  AlertTriangle, CheckCircle2, ExternalLink, Copy, RefreshCw,
} from "lucide-react";

/**
 * SocialPublishModal — Fase 2 modal: elegí plataformas (IG / FB / ambas),
 * editá el caption (con generador IA), previsualizá imagen + caption y publicá.
 *
 * Props:
 *   - image: { id, article_id, format, width, height, file_url }
 *   - article: { title, summary, main_category_name, city, ... }
 *   - onClose(): void
 *   - onPublished(result): void — llamado al finalizar (published/partial/failed)
 */
export default function SocialPublishModal({ image, article, onClose, onPublished, initialPlatforms }) {
  const isStory = image?.format === "story";
  const [platforms, setPlatforms] = useState(() => {
    const base = Array.isArray(initialPlatforms) && initialPlatforms.length ? initialPlatforms : ["instagram"];
    // Facebook Story requires a different API flow that we don't support yet
    return isStory ? base.filter((p) => p === "instagram") : base;
  });
  const [caption, setCaption] = useState("");
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [forceRepublish, setForceRepublish] = useState(false);

  // Auto-generate a starter caption from title + summary
  useEffect(() => {
    if (!article || caption) return;
    const t = article.title || "";
    const s = article.summary || "";
    const cat = article.main_category_name || "";
    const city = article.city || "";
    const tags = [
      "#SurPrimicia", "#TierraDelFuego",
      city ? `#${city.replace(/\s+/g, "").replace(/[áàä]/gi, "a").replace(/[éèë]/gi, "e").replace(/[íìï]/gi, "i").replace(/[óòö]/gi, "o").replace(/[úùü]/gi, "u").replace(/ñ/gi, "n")}` : null,
      cat ? `#${cat.replace(/\s+/g, "")}` : null,
    ].filter(Boolean).join(" ");
    const base = [t, "", s, "", "Más información en surprimicia.com.ar", "", tags].join("\n");
    setCaption(base);
  }, [article, caption]);

  const togglePlatform = (p) => {
    setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  const generateAi = async () => {
    if (!article) return;
    setGenerating(true);
    try {
      const res = await api.adminAiAssist({
        task: "social_caption",
        title: article.title || "",
        summary: article.summary || "",
        content: article.content || "",
        category: article.main_category_name || "",
        city: article.city || "",
        platform: platforms[0] || "instagram",
      });
      const c = res?.result?.caption;
      if (c) {
        setCaption(c);
        toast.success("Caption generado por IA");
      } else {
        toast.error("La IA no devolvió un caption válido");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo generar el caption");
    } finally {
      setGenerating(false);
    }
  };

  const doPublish = useCallback(async (force = false) => {
    if (!platforms.length) {
      toast.error("Elegí al menos una plataforma");
      return;
    }
    if (!caption.trim()) {
      toast.error("El caption no puede estar vacío");
      return;
    }
    setPublishing(true);
    setResult(null);
    try {
      const res = await api.adminSocialPublish({
        image_id: image.id,
        caption: caption.trim(),
        platforms,
        force,
      });
      if (res.status === "duplicate") {
        setDuplicates(res.duplicates);
        setPublishing(false);
        return;
      }
      setDuplicates(null);
      setResult(res);
      const okCount = (res.results || []).filter((r) => r.status === "published" || r.status === "test_mode").length;
      const failCount = (res.results || []).filter((r) => r.status === "failed").length;
      if (res.status === "published" && !res.test_mode) {
        toast.success(`Publicado en ${okCount} plataforma(s)`);
      } else if (res.test_mode) {
        toast.info(`Simulado (modo prueba) en ${okCount} plataforma(s)`);
      } else if (failCount) {
        toast.error(`${failCount} plataforma(s) fallaron`);
      }
      if (onPublished) onPublished(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo publicar");
    } finally {
      setPublishing(false);
    }
  }, [platforms, caption, image, onPublished]);

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  const remaining = 2200 - caption.length;
  const overLimit = remaining < 0;

  return (
    <div
      data-testid="social-publish-modal"
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-nightblue text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold">Publicar en Redes Sociales</div>
            <div className="text-[10px] uppercase tracking-widest text-white/70">
              {image?.format?.toUpperCase() || "POST"} · {image?.width}×{image?.height}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="social-publish-modal-close"
            className="text-white/80 hover:text-watergreen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT — preview */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-2">
              Vista previa
            </div>
            <div className="border border-softblue bg-lightgray/50 p-3 flex items-center justify-center">
              {image?.file_url && (
                <img
                  src={image.file_url}
                  alt="placa"
                  className="max-w-full max-h-[420px] object-contain shadow-md"
                />
              )}
            </div>
            <div className="mt-3 text-xs text-textgray">
              <div className="font-semibold text-blueblack line-clamp-2">
                {article?.title || "Sin título"}
              </div>
              <div className="mt-1 line-clamp-2">{article?.summary}</div>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="space-y-4">
            {/* Platforms */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-2">
                Plataforma {isStory && <span className="text-amber-700 normal-case font-normal ml-2">· Story — sólo Instagram (Facebook Story usa otra API)</span>}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => togglePlatform("instagram")}
                  data-testid="platform-instagram"
                  className={`flex-1 border p-3 flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-widest transition-colors ${
                    platforms.includes("instagram")
                      ? "border-pink-500 bg-pink-50 text-pink-800"
                      : "border-softblue text-textgray hover:bg-lightgray"
                  }`}
                >
                  <Instagram className="w-4 h-4" /> Instagram {isStory && "Story"}
                </button>
                <button
                  type="button"
                  onClick={() => !isStory && togglePlatform("facebook")}
                  disabled={isStory}
                  data-testid="platform-facebook"
                  className={`flex-1 border p-3 flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-widest transition-colors ${
                    isStory
                      ? "border-softblue text-textgray/40 bg-lightgray cursor-not-allowed"
                      : platforms.includes("facebook")
                      ? "border-[#1877F2] bg-blue-50 text-[#1877F2]"
                      : "border-softblue text-textgray hover:bg-lightgray"
                  }`}
                >
                  <Facebook className="w-4 h-4" /> Facebook
                </button>
              </div>
              {!isStory && (
                <button
                  type="button"
                  onClick={() => setPlatforms(["instagram", "facebook"])}
                  data-testid="platform-both"
                  className="mt-2 text-xs text-nightblue hover:text-turquoise underline"
                >
                  Publicar en ambas
                </button>
              )}
            </div>

            {/* Caption */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-textgray font-bold">
                  Texto para redes
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={copyCaption}
                    data-testid="caption-copy"
                    className="text-textgray hover:text-nightblue text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copiar
                  </button>
                  <Button
                    type="button"
                    onClick={generateAi}
                    disabled={generating || publishing}
                    data-testid="caption-generate-ai"
                    variant="outline"
                    className="h-7 rounded-none border-turquoise text-turquoise hover:bg-turquoise hover:text-white text-[10px] uppercase tracking-widest font-bold px-3"
                  >
                    {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    Generar con IA
                  </Button>
                </div>
              </div>
              <Textarea
                data-testid="caption-textarea"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={10}
                maxLength={2500}
                className="rounded-none border-softblue font-mono text-xs"
                placeholder="TÍTULO&#10;&#10;Bajada breve.&#10;&#10;Más información en surprimicia.com.ar&#10;&#10;#SurPrimicia #TierraDelFuego"
              />
              <div className={`text-[10px] uppercase tracking-widest text-right mt-1 font-mono ${overLimit ? "text-red-600" : "text-textgray"}`}>
                {caption.length} / 2200
                {overLimit && " · excede el límite de Instagram"}
              </div>
            </div>

            {/* Duplicate warning */}
            {duplicates && (
              <div
                data-testid="social-publish-duplicate"
                className="border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold mb-1">Esta noticia ya fue publicada</div>
                    {Object.entries(duplicates).map(([p, d]) => (
                      <div key={p} className="text-[11px]">
                        · {p} · {d?.published_at ? new Date(d.published_at).toLocaleString("es-AR") : "—"}
                        {d?.permalink && (
                          <a
                            href={d.permalink} target="_blank" rel="noopener noreferrer"
                            className="ml-1 inline-flex items-center gap-0.5 underline text-amber-900 hover:text-amber-700"
                          >ver <ExternalLink className="w-3 h-3" /></a>
                        )}
                      </div>
                    ))}
                    <label className="mt-2 flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={forceRepublish}
                        onChange={(e) => setForceRepublish(e.target.checked)}
                        data-testid="social-publish-force"
                        className="w-3.5 h-3.5 accent-amber-600"
                      />
                      <span>Publicar nuevamente (confirmar)</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Result */}
            {result && Array.isArray(result.results) && (
              <div
                data-testid="social-publish-result"
                className="border border-softblue bg-white p-3 text-xs space-y-1"
              >
                <div className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-1">
                  Resultado
                </div>
                {result.results.map((r, i) => {
                  const ok = r.status === "published" || r.status === "test_mode";
                  return (
                    <div key={i} className={`flex items-start gap-2 ${ok ? "text-emerald-700" : "text-red-700"}`}>
                      {ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1">
                        <span className="font-bold uppercase">{r.platform}</span>: {" "}
                        {r.status === "published" && "publicado"}
                        {r.status === "test_mode" && "simulado (modo prueba)"}
                        {r.status === "failed" && `${r.error_code} — ${r.error_message}`}
                        {r.permalink && (
                          <a
                            href={r.permalink} target="_blank" rel="noopener noreferrer"
                            className="ml-2 inline-flex items-center gap-0.5 underline"
                            data-testid={`social-publish-permalink-${r.platform}`}
                          >
                            ver publicación <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {r.external_media_id && (
                          <span className="ml-2 text-textgray font-mono text-[10px]">
                            ID: {r.external_media_id}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {result.test_mode && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-amber-700 font-bold border-t border-softblue pt-2">
                    Modo prueba activo — apagalo con SOCIAL_PUBLISHING_TEST_MODE=false para publicar real
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-softblue">
              <Button
                type="button"
                onClick={onClose}
                data-testid="social-publish-cancel"
                variant="outline"
                className="h-9 rounded-none border-softblue text-nightblue text-[11px] uppercase tracking-widest font-semibold"
              >
                Cerrar
              </Button>
              {duplicates && forceRepublish ? (
                <Button
                  type="button"
                  onClick={() => doPublish(true)}
                  disabled={publishing || overLimit || !platforms.length}
                  data-testid="social-publish-force-btn"
                  className="h-9 rounded-none bg-red-600 hover:bg-red-700 text-white text-[11px] uppercase tracking-widest font-bold px-4"
                >
                  {publishing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  Publicar de nuevo
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => doPublish(false)}
                  disabled={publishing || overLimit || !platforms.length}
                  data-testid="social-publish-btn"
                  className="h-9 rounded-none bg-turquoise hover:bg-nightblue text-white text-[11px] uppercase tracking-widest font-bold px-4"
                >
                  {publishing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Publicar ahora
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
